import React, { useEffect, useState } from 'react';
import {
  Card,
  Typography,
  Row,
  Col,
  Table,
  Tag,
  Modal,
  Statistic,
  Space,
  message,
  Empty,
  Button,
  Checkbox
} from 'antd';
import { WalletOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { walletService, type WalletSummary, type TopupTier, type WalletTransaction } from '../../services/walletService';
import { QuotationService } from '../../services/quotationService';
import { openRazorpayCheckout } from '../../config/razorpay';
import { useAuthStore } from '../../stores/authStore';

const { Title, Text, Paragraph } = Typography;

const fmt = (n: number, ccy: string = 'SGD') => `${ccy} ${Number(n).toFixed(2)}`;

const WalletPanel: React.FC = () => {
  const { user } = useAuthStore();
  const [wallet, setWallet] = useState<WalletSummary | null>(null);
  const [tiers, setTiers] = useState<TopupTier[]>([]);
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingTierId, setProcessingTierId] = useState<string | null>(null);
  // T&C must be accepted before starting a top-up (issue #61).
  const [terms, setTerms] = useState<string[]>([]);
  const [termsVisible, setTermsVisible] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [w, t, tx] = await Promise.all([
        walletService.getMyWallet(),
        walletService.listActiveTiers(),
        walletService.listTransactions(1, 20)
      ]);
      setWallet(w);
      setTiers(t);
      setTransactions(tx.items);
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Failed to load wallet');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  // Load T&C once for the agreement checkbox + modal.
  useEffect(() => {
    (async () => {
      try {
        const response = await QuotationService.getTermsAndConditions('topup');
        if (response.success && response.data?.terms) {
          setTerms(response.data.terms.filter((line): line is string => !!line && line.trim().length > 0));
        }
      } catch (err) {
        console.error('Error loading terms:', err);
      }
    })();
  }, []);

  // Start a top-up: create a Razorpay order, open Checkout, then refresh. The
  // wallet credit itself is applied by the backend webhook (`order.paid` with
  // order notes.kind === 'wallet_topup'), so after the modal succeeds we just
  // poll the balance a couple of times to cover the brief webhook race. Webhook
  // idempotency (keyed on the Razorpay payment id) makes repeated refreshes safe.
  const startTopup = async (tier: TopupTier) => {
    if (!agreedToTerms) {
      message.error('Please agree to the Terms and Conditions before topping up.');
      return;
    }
    setProcessingTierId(tier.TierID);
    try {
      const { orderId, keyId, amount, currency } = await walletService.createTopupOrder(tier.TierID);

      const result = await openRazorpayCheckout({
        keyId,
        orderId,
        amount,
        currency,
        name: 'Dasoon Eggs',
        description: `Wallet top-up — ${tier.Name}`,
        prefill: {
          name: user?.name || undefined,
          email: user?.email || undefined,
        },
      });

      // Verify + credit synchronously so the balance updates immediately (the
      // order.paid webhook remains an idempotent backstop).
      await walletService.verifyTopup({
        razorpay_order_id: result.razorpay_order_id,
        razorpay_payment_id: result.razorpay_payment_id,
        razorpay_signature: result.razorpay_signature,
      });

      message.success(`Top-up successful — ${fmt(tier.CreditAmount, wallet?.currency)} credited to your wallet.`);
      await loadAll();
      // Fallback refresh in case the webhook lands slightly later.
      setTimeout(loadAll, 3000);
    } catch (err: any) {
      // A plain dismiss is not an error worth shouting about.
      if (err?.message && err.message !== 'Payment cancelled') {
        message.error(err?.response?.data?.message || err.message || 'Failed to start top-up');
      }
    } finally {
      setProcessingTierId(null);
    }
  };

  const txColumns = [
    { title: 'Date', dataIndex: 'CreatedAt', key: 'CreatedAt', render: (d: string) => dayjs(d).format('YYYY-MM-DD HH:mm') },
    {
      title: 'Type', dataIndex: 'Type', key: 'Type',
      render: (t: WalletTransaction['Type']) => {
        const color = t === 'topup' ? 'green' : t === 'debit' ? 'red' : t === 'refund' ? 'orange' : 'blue';
        return <Tag color={color}>{t.toUpperCase()}</Tag>;
      }
    },
    {
      title: 'Amount', dataIndex: 'Amount', key: 'Amount', align: 'right' as const,
      render: (a: number, r: WalletTransaction) => {
        const sign = r.Type === 'debit' ? '-' : '+';
        return <Text strong style={{ color: r.Type === 'debit' ? '#cf1322' : '#3f8600' }}>{sign}{fmt(a, wallet?.currency)}</Text>;
      }
    },
    {
      title: 'Balance after', dataIndex: 'BalanceAfter', key: 'BalanceAfter', align: 'right' as const,
      render: (b: number) => fmt(b, wallet?.currency)
    },
    { title: 'Reference', dataIndex: 'RefID', key: 'RefID', render: (r: string | null) => r ?? '—' },
    { title: 'Description', dataIndex: 'Description', key: 'Description', render: (d: string | null) => d ?? '—' }
  ];

  return (
    <>
      <Card loading={loading} style={{ marginBottom: 24 }}>
        <Row gutter={[24, 24]}>
          <Col xs={24} md={10}>
            <Statistic
              title={<><WalletOutlined /> Wallet Balance</>}
              value={wallet?.balance ?? 0}
              precision={2}
              prefix={wallet?.currency ?? 'SGD'}
              valueStyle={{ color: '#1890ff', fontSize: 36 }}
            />
            {wallet && (
              <Text type="secondary">Last updated {dayjs(wallet.updatedAt).format('YYYY-MM-DD HH:mm')}</Text>
            )}
          </Col>
          <Col xs={24} md={14}>
            <Title level={4} style={{ marginTop: 0 }}>Top up your wallet</Title>
            <div style={{ marginBottom: 12 }}>
              <Checkbox
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
              >
                <Text type="danger" style={{ marginRight: 4 }}>*</Text>
                I agree to the{' '}
                <Button type="link" onClick={() => setTermsVisible(true)} style={{ padding: 0 }}>
                  Terms and Conditions
                </Button>
              </Checkbox>
            </div>
            {tiers.length === 0 ? (
              <Empty description="No top-up tiers available. Please check back later." />
            ) : (
              <Row gutter={[12, 12]}>
                {tiers.map(tier => {
                  const bonus = Number(tier.CreditAmount) - Number(tier.PayAmount);
                  const isProcessing = processingTierId === tier.TierID;
                  return (
                    <Col xs={24} sm={12} key={tier.TierID}>
                      <Card
                        size="small"
                        hoverable={!processingTierId}
                        onClick={() => !processingTierId && startTopup(tier)}
                        style={{ borderColor: bonus > 0 ? '#52c41a' : undefined, opacity: isProcessing ? 0.6 : 1 }}
                      >
                        <Text strong>{tier.Name}</Text>
                        <br />
                        <Text type="secondary">Pay {fmt(tier.PayAmount)}</Text>
                        <br />
                        <Text>Get <Text strong>{fmt(tier.CreditAmount)}</Text> credit</Text>
                        {bonus > 0 && (
                          <div><Tag color="green" icon={<PlusOutlined />}>{fmt(bonus)} bonus</Tag></div>
                        )}
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
          </Col>
        </Row>
      </Card>

      <Card title="Transaction history">
        <Table
          rowKey="TransactionID"
          dataSource={transactions}
          columns={txColumns}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'No transactions yet' }}
          size="small"
          scroll={{ x: 'max-content' }}
        />
      </Card>

      <Modal
        title="Terms and Conditions"
        open={termsVisible}
        onCancel={() => setTermsVisible(false)}
        footer={[
          <Button key="close" onClick={() => setTermsVisible(false)}>
            Close
          </Button>
        ]}
        width={600}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {terms.length > 0 ? (
            terms.map((line, idx) => <Paragraph key={idx}>{line}</Paragraph>)
          ) : (
            <Paragraph type="secondary">Terms and conditions are unavailable right now.</Paragraph>
          )}
        </Space>
      </Modal>
    </>
  );
};

export default WalletPanel;
